/*
 * A wrapper around Google Maps map class.
 * Provides some shorthand methods.
 *
 * @example
 *
 *   const center = {
 *     lat: 49.81749199999999,
 *     lng: 15.472962000000052
 *   }
 *
 *   const options = {
 *     zoom: 10,
 *     scrollwheel: false,
 *     streetViewControl: false,
 *     mapTypeControl: false,
 *     styles: {}
 *   }
 *
 *   new GoogleMap(el, center, options, map => doSomethingWithMap(map))
 *
 */

const googleMapsLoader = require('shared/javascripts/public_framework/services/google_maps_loader')

const DEFAULT_ZOOM = 13
const MARKER_SIZE = 43

module.exports = class GoogleMap {
  constructor (el, location = {}, options = {}, initCallback = () => { }) {
    this.el = el
    this.location = location
    this.options = options
    this.initCallback = initCallback
    this.markerPath = this.options.markerPath

    googleMapsLoader.onReady(this.initMapBlock.bind(this))
  }

  initMapBlock () {
    this.map = new window.google.maps.Map(this.el, this.options)
    this.geocoder = new window.google.maps.Geocoder()

    if (this.options.zoom === undefined) { this.options.zoom = DEFAULT_ZOOM }

    if (this.location.address) {
      this.initWithAddress(this.location.address)
    } else if ((this.location.lat !== undefined) && (this.location.lng !== undefined)) {
      this.initWithCoords(this.location.lat, this.location.lng)
    }

    this.initCallback(this)
  }

  initWithAddress (address) {
    this.geocoder.geocode({ address }, this.onGeocodeResultsUpdate.bind(this))
  }

  onGeocodeResultsUpdate (results, status) {
    if (status !== window.google.maps.GeocoderStatus.OK) return
    this.map.fitBounds(results[0].geometry.viewport)
    this.map.setCenter(results[0].geometry.location)
  }

  initWithCoords (lat, lng) {
    this.map.setCenter(new window.google.maps.LatLng(lat, lng))
    this.map.setZoom(this.options.zoom)
  }

  addMarker (position, markerPath, markerWidth = MARKER_SIZE, markerHeight = MARKER_SIZE) {
    if (markerPath == null) { ({ markerPath } = this) }
    const size = new window.google.maps.Size(markerWidth, markerHeight)
    const icon = new window.google.maps.MarkerImage(markerPath, null, null, null, size)

    return new window.google.maps.Marker({ position, map: this.map, icon })
  }

  makeCoords (lat, lng) {
    return new window.google.maps.LatLng(lat, lng)
  }

  fitCoords (locations) {
    const bounds = new window.google.maps.LatLngBounds()
    locations.forEach(l => bounds.extend(l))
    this.map.fitBounds(bounds)
  }

  setCenter (location) {
    this.map.setCenter(location)
  }

  setZoom (zoom) {
    this.map.setZoom(zoom)
  }

  geocodeLocation (location) {
    const { latitude, longitude, address } = location

    if (latitude && longitude) {
      return Promise.resolve(this.makeCoords(latitude, longitude))
    }

    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ address }, function (results, status) {
        if (status !== window.google.maps.GeocoderStatus.OK) {
          return resolve(null)
        }
        return resolve(results[0].geometry.location)
      })
    })
  }

  geocodeLocations (locations) {
    return Promise.all(
      locations.map(l => this.geocodeLocation(l))
    ).then(coordinates => coordinates.filter(c => !!c))
  }

  getViewportData () {
    return {
      zoom: this.map.getZoom(),
      bounds: this.map.getBounds()
    }
  }

  getGoogleMap () {
    return this.map
  }
}



// WEBPACK FOOTER //
// ./front/apps/shared/javascripts/public_framework/services/google_map.js